"use client";

import { useLocale, useTranslations } from "next-intl";
import styles from "./home-searchbar.module.css";
import SelectorComponent from "./selector-component";
import { useEffect, useRef, useState, useTransition } from "react";
import { AutocompleteSuggestion, PurposeEnum, TypeEnum } from "@/types/types";
import { CircleFlag } from "react-circle-flags";
import { useSearchbarStore } from "./useSearchbarStore";
import SelectorComponentListItem from "./selector-component-list-item";
import { MdApartment, MdBusiness, MdHouse, MdLandscape } from "react-icons/md";
import { listingsNumberFormatter } from "@/lib/price-formatter";
import { useRouter } from "@/i18n/navigation";
import { toast } from "react-toastify";
import { getFilteredListingCountFromAutocomplete } from "@/lib/getFilteredLocationsData/autocomplete_data";
import { buildSearchParamsAsDeafult } from "@/lib/buildSearchParams/buildSearchParams";
import { BsHousesFill } from "react-icons/bs";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import { HomeOutlined, KeyOutlined, Search } from "@mui/icons-material";
import { IoIosConstruct } from "react-icons/io";

export default function HomeSearchbar({
  lat,
  long,
}: {
  lat: string | null;
  long: string | null;
}) {
  const {
    text,
    onTextChange,
    suggestions,
    currentSuggestion,
    setCurrentSuggestion,
    setIsSuggestionsOpen,
    isSuggestionsOpen,
    search,
    setType,
    setPurpose,
    validateFields,
    fieldsErrors,
    validatePurposeField,
    validateTypeField,
    validateWhereField,
  } = useSearchbarStore();

  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  const t = useTranslations("HomeSearchbar");
  const locale = useLocale();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionListRef = useRef<HTMLUListElement>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const suggestionsValuesRef = useRef<AutocompleteSuggestion[]>([]);
  const router = useRouter();

  const handleDivClick = () => {
    inputRef.current?.focus();
  };

  useEffect(() => {
    suggestionsValuesRef.current = suggestions;
  }, [suggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionListRef.current &&
        !suggestionListRef.current.contains(event.target as Node)
      ) {
        setCurrentSuggestion(suggestionsValuesRef.current[0], locale);
        setIsSuggestionsOpen(false);
        validateWhereField();
      }
    };
    if (isSuggestionsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSuggestionsOpen]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => setIsInputFocused(true);
    const handleBlur = () => setIsInputFocused(false);

    input.addEventListener("focus", handleFocus);
    input.addEventListener("blur", handleBlur);

    return () => {
      input.removeEventListener("focus", handleFocus);
      input.removeEventListener("blur", handleBlur);
    };
  }, []);

  return (
    <form
      className={styles.searchbarContainer}
      role="search"
      aria-label={t("searchFormLabel")}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        validateFields();
        const { currentSuggestion, fieldsErrors, search } =
          useSearchbarStore.getState();
        if (
          currentSuggestion === null ||
          search.location_url === "" ||
          fieldsErrors.where === true ||
          fieldsErrors.type === true ||
          fieldsErrors.purpose === true
        ) {
          toast("Plese complete all search form fields.", { type: "error" });
          return;
        }
        const queryParams = buildSearchParamsAsDeafult(
          search,
          locale,
          "",
          currentSuggestion.names[locale],
          listingsNumberFormatter(
            getFilteredListingCountFromAutocomplete(
              currentSuggestion,
              search.type,
              search.purpose,
            ),
          ),
        );
        startTransition(() => {
          router.push({
            pathname: makeLocalizedHrefNoLocale(locale, "search", [
              search.location_url,
              search.type,
              search.purpose,
              search.page_number.toString(),
            ]),
            query: queryParams,
          });
        });
      }}
    >
      {/* --- TOP TABS: PURPOSE --- */}
      <div className={styles.tabsRow}>
        <button
          type="button"
          className={`${styles.tabButton} ${
            search.purpose === PurposeEnum.FORSALE ? styles.activeTab : ""
          }`}
          onClick={(e) => {
            e.preventDefault();
            setPurpose(PurposeEnum.FORSALE);
            validatePurposeField();
          }}
        >
          <HomeOutlined className={styles.tabIcon} />
          {t("for-sale")}
        </button>

        <button
          type="button"
          className={`${styles.tabButton} ${
            search.purpose === PurposeEnum.FORRENT ? styles.activeTab : ""
          }`}
          onClick={(e) => {
            e.preventDefault();
            setPurpose(PurposeEnum.FORRENT);
            validatePurposeField();
          }}
        >
          <KeyOutlined className={styles.tabIcon} />
          {t("for-rent")}
        </button>
        <button
          type="button"
          className={`${styles.tabButton} ${
            search.purpose === PurposeEnum.FORRENT ? styles.activeTab : ""
          }`}
          onClick={(e) => {
            e.preventDefault();
            setPurpose(PurposeEnum.FORRENT);
            validatePurposeField();
          }}
        >
          <IoIosConstruct className={styles.tabIcon} />
          New Development
        </button>
      </div>

      {/* --- BOTTOM ROW: INPUTS --- */}
      <div className={styles.inputsRow}>
        {/* 1. Location */}
        <div className={styles.whereColumnWrapper}>
          <div
            className={`${styles.whereColumn} ${
              isInputFocused ? styles.isActive : ""
            }`}
            onClick={handleDivClick}
          >
            <div className={styles.whereTextWrapper}>
              <label
                className={`${styles.text} ${
                  fieldsErrors.where === true && styles.hasError
                }`}
              >
                {t("where")}
              </label>
              <div className={styles.inputAndNumberWrapper}>
                <input
                  id="whereInput"
                  className={`${styles.input} ${text !== "" && styles.hasText} ${
                    fieldsErrors.where === true && styles.hasError
                  }`}
                  ref={inputRef}
                  type="text"
                  value={text}
                  placeholder={t("whereInput")}
                  aria-expanded={isSuggestionsOpen}
                  aria-controls="suggestions-list"
                  aria-invalid={fieldsErrors.where || false}
                  role="combobox"
                  onChange={(e) => onTextChange(e, locale, lat, long)}
                  autoComplete="off"
                />
                {currentSuggestion !== null &&
                  text === currentSuggestion.names[locale] && (
                    <span
                      className={`${styles.listingsNumber} ${
                        text !== "" && styles.hasText
                      }`}
                    >
                      (
                      {currentSuggestion !== null &&
                        listingsNumberFormatter(
                          getFilteredListingCountFromAutocomplete(
                            currentSuggestion,
                            search.type,
                            search.purpose,
                          ),
                        )}
                      )
                    </span>
                  )}
              </div>
            </div>
          </div>

          {/* Autocomplete Dropdown */}
          {isSuggestionsOpen && (
            <ul
              id="suggestions-list"
              className={styles.suggestionsList}
              ref={suggestionListRef}
              role="listbox"
            >
              {suggestions?.map((suggestion, index) => {
                return (
                  <li
                    key={index}
                    role="option"
                    aria-selected={currentSuggestion?.slug === suggestion.slug}
                  >
                    <button
                      type="button"
                      className={`${styles.listButton} ${
                        currentSuggestion?.slug === suggestion.slug &&
                        styles.isActive
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentSuggestion(suggestion, locale);
                        validateWhereField();
                      }}
                    >
                      <span className={styles.flagAndNameWrapper}>
                        {suggestion.country_code && (
                          <CircleFlag
                            className={styles.countryFlag}
                            countryCode={suggestion.country_code?.toLowerCase()}
                          />
                        )}
                        <span className={styles.name}>
                          {suggestion.names[locale] ?? suggestion.names["en"]}
                        </span>
                      </span>
                      <span className={styles.number}>
                        (
                        {listingsNumberFormatter(
                          getFilteredListingCountFromAutocomplete(
                            suggestion,
                            search.type,
                            search.purpose,
                          ),
                        )}
                        )
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          className={`${styles.separator} ${styles.desktopOnlySeparator}`}
        ></div>

        {/* 2 & 3. Grouped Selectors (Type & Price) */}
        <div className={styles.selectorsGroup}>
          <div className={styles.selectorBox}>
            <SelectorComponent
              title={t("type")}
              description={
                search.type === TypeEnum.DEFAULT
                  ? t("typeInput")
                  : t(search.type)
              }
              hasSelection={search.type !== TypeEnum.DEFAULT}
              isOpen={isTypeOpen}
              hasError={fieldsErrors.type}
              setIsOpen={() => setIsTypeOpen(!isTypeOpen)}
            >
              <SelectorComponentListItem
                text={t("apartment-house")}
                icon={<BsHousesFill />}
                isActive={search.type === TypeEnum.APARTMENT_HOUSE}
                onClick={() => {
                  setIsTypeOpen(false);
                  setType(TypeEnum.APARTMENT_HOUSE);
                  validateTypeField();
                }}
              />
              <SelectorComponentListItem
                text={t("apartment")}
                icon={<MdApartment />}
                isActive={search.type === TypeEnum.APARTMENT}
                onClick={() => {
                  setIsTypeOpen(false);
                  setType(TypeEnum.APARTMENT);
                  validateTypeField();
                }}
              />
              <SelectorComponentListItem
                text={t("house")}
                icon={<MdHouse />}
                isActive={search.type === TypeEnum.HOUSE}
                onClick={() => {
                  setIsTypeOpen(false);
                  setType(TypeEnum.HOUSE);
                  validateTypeField();
                }}
              />
              <SelectorComponentListItem
                text={t("land")}
                icon={<MdLandscape />}
                isActive={search.type === TypeEnum.LAND}
                onClick={() => {
                  setIsTypeOpen(false);
                  setType(TypeEnum.LAND);
                  validateTypeField();
                }}
              />
              <SelectorComponentListItem
                text={t("office")}
                icon={<MdBusiness />}
                isActive={search.type === TypeEnum.OFFICE}
                onClick={() => {
                  setIsTypeOpen(false);
                  setType(TypeEnum.OFFICE);
                  validateTypeField();
                }}
              />
            </SelectorComponent>
          </div>

          <div className={`${styles.separator} ${styles.groupSeparator}`}></div>

          <div className={styles.selectorBox}>
            <SelectorComponent
              title="Preț"
              description="Oricare"
              hasSelection={false}
              isOpen={isPriceOpen}
              hasError={false}
              setIsOpen={() => setIsPriceOpen(!isPriceOpen)}
            >
              <div
                style={{
                  padding: "16px",
                  color: "var(--v2-text-secondary)",
                  fontSize: "14px",
                }}
              >
                Price slider implementation goes here.
              </div>
            </SelectorComponent>
          </div>
        </div>

        <div
          className={`${styles.separator} ${styles.desktopOnlySeparator}`}
        ></div>

        {/* 4. Search Button */}
        <button className={styles.searchButton} type="submit">
          <Search className={styles.searchIcon} />
          {t("searchButton")}
        </button>
      </div>
    </form>
  );
}
